/**
 * One consolidated, safe-to-display summary of a user's subscription state,
 * plus the rules for spotting when two accounts probably belong to the same
 * person.
 *
 * Why this exists (#4248): support needs the same consolidated view at the top
 * of every subscription ticket, and the ticket requires the Admin UI and the
 * `firebase-cmd` script to stay at deliberate feature parity. Before this
 * module they each hand-rolled their own summary — the admin page in
 * `updateCurrentStateSummary()` and the CLI in `provLine` / `subInfo_async` —
 * and the two had already drifted apart. Everything here is a pure function
 * over the stored user doc, so both front-ends render the same facts and
 * cannot disagree.
 *
 * SECURITY: this summary is printed to an admin screen and to a terminal, so
 * it is built as an explicit **allow-list**. It copies only the named
 * non-secret fields out of the user doc and never spreads a whole block. The
 * user doc also carries password material (`derived_key`, `salt`,
 * `password_*`), a PayPro `invoiceUrl`, an Apple `siwaRefreshToken`, a
 * redeemable `keyData.activationCode` and the Google Play `purchaseToken` —
 * none of those may ever appear here. The Google Play case is the subtle one:
 * that provider's "subscription id" IS the purchase token, which is a
 * credential, so the summary reports only whether one is present.
 */
import {
  CouchUserInfo,
  CouchUserPaddleScheduledChange,
  NEVER_EXPIRES,
  SubscriptionStatus,
  UserLicenseType,
} from './data-model/couch-user';
import { CoachSlotCounts } from './data-model/coach';
import { resolveSubscriptionEndInstants } from './data-model/subscription-end-instants';

/** One provider block, reduced to the fields that are safe to show. */
export interface SubscriptionOpsBlockSummary {
  provider: UserLicenseType;
  status?: SubscriptionStatus;
  /** True for the block matching the user's current `licenseType`. The others
   *  are historical — a user who moved from PayPro to Paddle keeps both. */
  isActiveProvider: boolean;
  productId?: string;
  expires_utc?: string;
  /** A pending Paddle cancel / pause / resume on THIS block. Only the two
   *  Paddle blocks — the personal one and the coaching seat — can carry one
   *  (#4418). Per block, because a coach can hold both, each with its own. */
  scheduledChange?: CouchUserPaddleScheduledChange;
}

export interface SubscriptionOpsSummary {
  /** The user's current `licenseType`, or undefined when they have none. */
  provider?: UserLicenseType;
  status?: SubscriptionStatus;
  /** The provider block's RAW stored in-trial flag. Undefined means "the block
   *  keeps no such flag" — PayPro does not, so it must not be reported as
   *  `false`.
   *
   *  This is deliberately NOT the canonical answer to "is this person in a free
   *  trial". That is `isSubscriberInFreeTrial` in `./data-model/couch-user`,
   *  which exists because the raw flag is not enough (#4184: Paddle can leave
   *  it unset during a real trial, and PayPro expresses a trial as a future
   *  `trialEnds_utc`). The canonical rule needs a clock, and this builder is
   *  deliberately pure, so it reports the stored flag and names the authority
   *  here. A caller that must decide entitlement should use that function. */
  inTrial?: boolean;
  /** Expiry / next charge for the ACTIVE provider, taken from its own block
   *  and falling back to the reconciled top-level value for the license types
   *  that keep no per-block expiry. For PayPro this is our reconciled expiry,
   *  not PayPro's authoritative next-charge date; a caller holding the live
   *  `nextPayment` should prefer that for display. */
  expires_utc?: string;
  /** The reconciled top-level `licenseExpires_utc`. Kept separate from
   *  `expires_utc` because the two legitimately differ: provider blocks add a
   *  grace period at write time and the reconciler does not. Both front-ends
   *  show both, labelled, rather than silently picking one. */
  topLevelExpires_utc?: string;
  /** True when `expires_utc` is the indefinite-licence sentinel rather than a
   *  real date, so callers do not try to parse or format it. */
  neverExpires: boolean;
  /** The provider-side subscription identifier, where it is safe to show.
   *  Always undefined for Google Play — see the security note above. */
  providerSubscriptionId?: string;
  /** Why `providerSubscriptionId` is absent, when the reason is that the id is
   *  a credential we refuse to show rather than that there is none. Computed
   *  here so both front-ends explain it identically and neither has to re-derive
   *  which providers hide their id. Undefined means "there genuinely is no id". */
  providerSubscriptionIdHiddenReason?: string;
  /** A pending Paddle cancel / pause / resume on the block the Cancel and
   *  Resume actions act on: the coaching seat when `licenseType` is
   *  `CoachSeat` (#4418), otherwise the personal Paddle block. Never the other
   *  block's — in the become-a-coach handoff window the personal block carries
   *  its own pending cancel, which is not the one Resume would undo. */
  scheduledChange?: CouchUserPaddleScheduledChange;
  /** Every provider block present on the doc, active or historical. */
  blocks: SubscriptionOpsBlockSummary[];
}

/**
 * Everything the summary needs to know about ONE payment rail, in one place.
 *
 * Each rail used to be keyed on the provider three separate times — the block
 * list, the headline fields and the hidden-id explanation — with nothing making
 * the three agree (deferred finding df-33492a4f8991). One of those facts is a
 * security rule, so it matters that a new rail states all of them together.
 */
interface RailRules {
  /** The rail's stored block, which supplies its status and expiry. */
  block(user: CouchUserInfo): { subscriptionStatus?: SubscriptionStatus, expiresDate_utc?: string } | undefined;
  /** The field naming the plan the user is on, reported as `productId`. */
  planId(user: CouchUserInfo): string | undefined;
  /** An id used ONLY to decide whether the block is empty. Never displayed:
   *  for Google Play it differs from what `shownId` would show. */
  presenceId(user: CouchUserInfo): string | undefined;
  /** The block's raw in-trial flag. Undefined for a rail that keeps none, so
   *  it reads as "unknown" rather than `false`. */
  inTrial(user: CouchUserInfo): boolean | undefined;
  /**
   * SECURITY — `shownId` and `hiddenIdReason` are one rule and are written
   * together. `shownId` is the provider-side id, only where it is safe to show.
   * When a rail's id is a credential (the Google Play purchase token, a
   * redeemable activation code), `shownId` answers undefined and
   * `hiddenIdReason` says why, whenever that credential is present — so an
   * operator can tell "hidden id" from "no id".
   */
  shownId(user: CouchUserInfo): string | undefined;
  hiddenIdReason(user: CouchUserInfo): string | undefined;
  /** A pending Paddle cancel / pause / resume on this block (#4418). */
  scheduledChange(user: CouchUserInfo): CouchUserPaddleScheduledChange | undefined;
}

const GOOGLE_PLAY_TOKEN_NOTE = 'Google Play purchase token — not shown';

const noValue = (): undefined => undefined;

/** One entry per rail. A full `Record`, so a new `UserLicenseType` that is not
 *  given its rules here fails to compile. */
const RAIL_RULES: Record<UserLicenseType, RailRules> = {
  [UserLicenseType.PayPro]: {
    block: user => user.payProData,
    planId: user => user.payProData?.productId,
    presenceId: user => user.payProData?.subscriptionId,
    // PayPro stores no in-trial flag, so it stays undefined ("unknown").
    inTrial: noValue,
    shownId: user => user.payProData?.subscriptionId,
    hiddenIdReason: noValue,
    scheduledChange: noValue,
  },
  [UserLicenseType.Paddle]: {
    block: user => user.paddleData,
    // Paddle identifies the plan by price id rather than a product id.
    planId: user => user.paddleData?.priceId,
    presenceId: user => user.paddleData?.subscriptionId,
    inTrial: user => user.paddleData?.inTrialPeriod,
    shownId: user => user.paddleData?.subscriptionId,
    hiddenIdReason: noValue,
    scheduledChange: user => user.paddleData?.scheduledChange,
  },
  [UserLicenseType.Apple]: {
    block: user => user.appleData,
    planId: user => user.appleData?.productId,
    presenceId: user => user.appleData?.originalTransactionId,
    inTrial: user => user.appleData?.inTrialPeriod,
    // the original transaction id is Apple's stable subscription handle and
    // is not a credential, unlike the Google Play purchase token.
    shownId: user => user.appleData?.originalTransactionId,
    hiddenIdReason: noValue,
    scheduledChange: noValue,
  },
  [UserLicenseType.GooglePlay]: {
    block: user => user.googlePlayData,
    planId: user => user.googlePlayData?.productId ?? user.googlePlayData?.basePlanId,
    presenceId: user => user.googlePlayData?.orderId,
    inTrial: user => user.googlePlayData?.inTrialPeriod,
    // the subscription handle IS the purchase token — see the security note at
    // the top. Never shown; its presence is explained instead.
    shownId: noValue,
    hiddenIdReason: user => user.googlePlayData?.purchaseToken ? GOOGLE_PLAY_TOKEN_NOTE : undefined,
    scheduledChange: noValue,
  },
  [UserLicenseType.CoachTier]: {
    block: user => user.coachTierData,
    planId: user => user.coachTierData?.productId,
    presenceId: noValue,
    inTrial: noValue,
    // a coach tier lives in a store; Apple's handle is safe to show, Google
    // Play's is the purchase token, so it stays hidden for the same reason.
    shownId: user => user.coachTierData?.provider === 'apple'
      ? user.coachTierData?.originalTransactionId
      : undefined,
    hiddenIdReason: user => user.coachTierData?.provider === 'googlePlay' && user.coachTierData?.purchaseToken
      ? GOOGLE_PLAY_TOKEN_NOTE
      : undefined,
    scheduledChange: noValue,
  },
  [UserLicenseType.CoachSeat]: {
    block: user => user.coachSeatData,
    planId: user => user.coachSeatData?.priceId,
    presenceId: user => user.coachSeatData?.subscriptionId,
    inTrial: noValue,
    shownId: user => user.coachSeatData?.subscriptionId,
    hiddenIdReason: noValue,
    scheduledChange: user => user.coachSeatData?.scheduledChange,
  },
  [UserLicenseType.Key]: {
    block: user => user.keyData,
    planId: noValue,
    presenceId: noValue,
    inTrial: noValue,
    // the activation code identifies the grant but is redeemable, so it is
    // never shown as an identifier, and never used as one here either.
    shownId: noValue,
    hiddenIdReason: user => user.keyData?.activationCode ? 'activation code — not shown' : undefined,
    scheduledChange: noValue,
  },
  [UserLicenseType.Coach]: {
    block: user => user.coachData,
    planId: noValue,
    presenceId: noValue,
    inTrial: noValue,
    shownId: noValue,
    hiddenIdReason: noValue,
    scheduledChange: noValue,
  },
};

/** The block list is reported in the order the rails are written in
 *  `RAIL_RULES` above. Taking the order from the table itself, rather than from
 *  a second list, means a rail cannot have rules and still be left out of the
 *  block list. String keys keep their insertion order in JavaScript. */
const RAIL_ORDER = Object.keys(RAIL_RULES) as UserLicenseType[];

/**
 * Build the consolidated summary. Pure — no I/O, no clock, no mutation of the
 * passed user.
 */
export function buildSubscriptionOpsSummary(user: CouchUserInfo): SubscriptionOpsSummary {
  const activeProvider = user.licenseType;

  // collect every provider block that exists on the doc, so support can see a
  // leftover block from a provider the user has since migrated away from.
  // ALL license-granting blocks are covered, including the coach rails and an
  // activation key — a coach billed through the Paddle per-seat rail is
  // exactly the kind of account whose billing is hardest to reason about, so
  // an empty summary there would be worse than useless.
  const blocks: SubscriptionOpsBlockSummary[] = [];
  for (const provider of RAIL_ORDER) {
    const rules = RAIL_RULES[provider];
    const block = rules.block(user);
    if (!block) {
      continue;
    }
    const productId = rules.planId(user);
    // skip a block that carries no actual subscription. `normalizeCouchUserInfo`
    // ALWAYS creates an empty `payProData` with status None, so without this a
    // normalized doc (what the admin page reads) would show a phantom
    // "PayPro: status=none" line that the CLI — which reads the raw doc — never
    // shows. That would be the two front-ends disagreeing, which is the exact
    // thing this module exists to prevent. It would also make the block list
    // meaningless, since PayPro would appear for every user.
    const isEmptyBlock = (block.subscriptionStatus === undefined
      || block.subscriptionStatus === SubscriptionStatus.None)
      && !block.expiresDate_utc && !productId && !rules.presenceId(user);
    if (isEmptyBlock) {
      continue;
    }
    blocks.push({
      provider,
      status: block.subscriptionStatus,
      isActiveProvider: activeProvider === provider,
      productId,
      expires_utc: block.expiresDate_utc,
      scheduledChange: rules.scheduledChange(user),
    });
  }

  // pull the headline fields from whichever rail is active. A doc with no
  // `licenseType`, or one this version does not know, has no rules to read.
  // the own-property check matters: a damaged doc can carry a value such as
  // 'constructor', which a plain lookup would find on Object.prototype.
  const activeRules: RailRules | undefined = activeProvider
    && Object.prototype.hasOwnProperty.call(RAIL_RULES, activeProvider)
    ? RAIL_RULES[activeProvider]
    : undefined;
  const inTrial = activeRules?.inTrial(user);
  const providerSubscriptionId = activeRules?.shownId(user);

  // fall back to the reconciled top-level expiry when the active block keeps
  // none, so an indefinite grant still reads as indefinite instead of blank.
  const resolvedExpires = activeRules?.block(user)?.expiresDate_utc ?? user.licenseExpires_utc;

  // explain a deliberately-hidden id. Without this an operator sees a blank
  // field and cannot tell "no id" from "hidden id".
  const providerSubscriptionIdHiddenReason = providerSubscriptionId
    ? undefined
    : activeRules?.hiddenIdReason(user);

  return {
    provider: activeProvider,
    status: user.subscriptionStatus,
    inTrial,
    expires_utc: resolvedExpires,
    topLevelExpires_utc: user.licenseExpires_utc,
    neverExpires: resolvedExpires === NEVER_EXPIRES,
    providerSubscriptionId,
    providerSubscriptionIdHiddenReason,
    scheduledChange: activeProvider === UserLicenseType.CoachSeat
      ? user.coachSeatData?.scheduledChange
      : user.paddleData?.scheduledChange,
    blocks,
  };
}

/**
 * What a Cancel at period end does on an account that holds a coaching seat
 * (#4418) — the blast radius the operator must see BEFORE confirming, because
 * ending a coach's subscription also ends Premium for every client they train.
 *
 * Built here so the Admin page and `sub:cancel` word it identically, from the
 * one server-computed count (`adminGetUserDetails`'s `coachClientCounts`).
 */
export interface CoachCancelImpact {
  /** Set when the cancel must be REFUSED: the client count could not be read,
   *  so nobody can say who the cancel affects. Never shown as "0 clients". */
  refusal?: string;
  /** The sentences to show when the cancel may go ahead. */
  description: string;
}

/**
 * Describe what a Cancel at period end would do for this account, or answer
 * `undefined` when the account holds no coaching seat and the ordinary
 * personal-subscriber wording applies.
 *
 * Keyed on the RESOLVED rail (`licenseType`), the same thing the server's
 * cancel dispatch keys on — never on "holds a coach block". During the
 * become-a-coach handoff a coach can hold a live personal subscription AND a
 * live seat, and the cancel then acts on whichever wins the latest-expiry
 * reconcile. Quoting the client count while the personal one is cancelled would
 * describe one subscription and cancel another.
 *
 * Only the coach-seat rail can refuse on a missing count: when the personal
 * block is the one being cancelled, the coach's client count is irrelevant and a
 * failure to read it must not block that cancel.
 */
export function describeCoachCancelImpact(
  user: CouchUserInfo,
  counts: CoachSlotCounts | undefined,
  countsError: string | undefined,
): CoachCancelImpact | undefined {
  if (!user.coachSeatData) {
    return undefined;
  }

  if (user.licenseType !== UserLicenseType.CoachSeat) {
    // the handoff window: the personal subscription won the reconcile, so the
    // cancel acts on it. Say so, and quote no client count.
    return {
      description: `This cancels the customer's PERSONAL ${user.licenseType ?? 'unknown'} subscription, NOT their `
        + 'coaching subscription, which is left running. The coaching subscription can only be cancelled here '
        + 'once it is the active one — after the personal subscription has lapsed.',
    };
  }

  if (countsError !== undefined || !counts) {
    return {
      refusal: 'We could not read how many clients this coach has'
        + (countsError ? ` (${countsError})` : ' (no count was returned)')
        + ', so we cannot tell you whose Premium this cancel would end. Nothing was cancelled — try again.',
      description: '',
    };
  }

  // two different questions, both worth answering: when Paddle stops the
  // subscription, and when people actually lose access (the stored expiry, which
  // carries our grace). See `resolveSubscriptionEndInstants` for why they differ.
  const instants = resolveSubscriptionEndInstants(UserLicenseType.CoachSeat, user.coachSeatData.expiresDate_utc, user);
  const whenItEnds = instants.providerActsAt
    ? `Paddle ends it at the end of the current billing period (${instants.providerActsAt.toISO()}).`
    : 'Paddle ends it at the end of the current billing period.';
  const accessUntil = instants.accessEndsAt ? ` until ${instants.accessEndsAt.toISO()}` : ' until then';

  const clients = counts.claimed === 0
    ? 'This coach has no clients yet, so no client loses Premium.'
    : `${counts.claimed} client(s) will lose Premium when it ends; they and the coach keep it${accessUntil}.`;
  const invites = counts.outstandingInvites > 0
    ? ` ${counts.outstandingInvites} pending invite(s) that nobody has accepted will stop granting Premium at the same time.`
    : '';
  const hasPersonalSubscription = !!(user.paddleData?.subscriptionId || user.appleData?.originalTransactionId
    || user.googlePlayData?.purchaseToken || user.payProData?.subscriptionId);
  const personalNote = hasPersonalSubscription
    ? ' The customer\'s personal subscription, if still active, is not affected.'
    : '';

  return {
    description: 'This cancels the COACHING subscription, billed per client seat through Paddle. '
      + `${whenItEnds} ${clients}${invites}${personalNote}`,
  };
}

/**
 * The cost warning for resuming a PAUSED coaching seat (#4418), or `undefined`
 * when there is none to give. A paused seat has no paid time left, so Paddle
 * restarts it with a fresh billing period and charges at once, at the current
 * seat count. The coach-seat Cancel refusal sends support to "resume it first",
 * so the operator must see that charge at the moment they click — on the Admin
 * page and in `sub:resume` alike.
 */
export function describeCoachResumeCost(user: CouchUserInfo): string | undefined {
  const seat = user.coachSeatData;
  if (user.licenseType !== UserLicenseType.CoachSeat || seat?.subscriptionStatus !== SubscriptionStatus.Paused) {
    return undefined;
  }
  const seats = seat.quantity !== undefined ? ` (${seat.quantity} seat(s) on record)` : '';
  return 'This coaching subscription is PAUSED. Resuming it starts a new billing period straight away, and Paddle '
    + `charges the coach for it at the current seat count${seats}.`;
}

// ---------------------------------------------------------------------------
// Related-account matching
//
// Duplicate accounts (#4227, #4198, #4207) usually come from the same person
// signing up twice with addresses that differ only cosmetically, or from a
// payment provider creating a second account. Matching has to be deliberately
// conservative: a false positive here would invite an operator to move a
// subscription onto a DIFFERENT person's account.
// ---------------------------------------------------------------------------

/** Domains that ignore dots in the local part, so `first.last` and `firstlast`
 *  are genuinely the same mailbox. Everywhere else dots are significant and
 *  stripping them would merge two unrelated people. The value each maps to is
 *  the canonical spelling: `googlemail.com` is an alias of `gmail.com`, so the
 *  two must reduce to one string or a person split across both spellings would
 *  never be matched. */
const DOT_INSENSITIVE_DOMAIN_CANONICAL: Record<string, string> = {
  'gmail.com': 'gmail.com',
  'googlemail.com': 'gmail.com',
};

/**
 * Reduce an address to the form used for comparing two accounts.
 *
 * Lower-cases and trims, drops any `+suffix` tag, and drops dots ONLY on the
 * domains that genuinely ignore them. Returns undefined for anything that is
 * not a recognisable address, so a malformed value can never match another one.
 *
 * **Why the `+tag` rule is not domain-gated, unlike the dot rule.** Dropping a
 * tag is applied everywhere because subaddressing is near-universal (Gmail,
 * Outlook, Fastmail, iCloud, most corporate mail) and because the failure it
 * risks is mild: the two addresses are shown side by side with the reason for
 * the match, so an operator sees the evidence before acting. Dot-stripping is
 * gated because its failure is severe — on most providers `first.last` and
 * `firstlast` are two different people, so collapsing them would be a
 * confident, wrong claim. The known residual risk is a self-hosted or
 * corporate mail server that treats `bob+shop@corp.com` as a genuinely
 * separate mailbox; that is accepted, and it is why nothing in this tooling
 * acts on a match automatically.
 *
 * Related but different: `normalizeEmail` in `./email-normalization` canonicalizes
 * an address for STORAGE (trim + lower-case only). This function reduces one
 * for FUZZY MATCHING and deliberately loses information, so it must never be
 * used as the value written to a user record.
 */
export function normalizeEmailForAccountMatch(email: string | undefined): string | undefined {
  if (!email) {
    return undefined;
  }
  const trimmed = email.trim().toLowerCase();
  const atIndex = trimmed.lastIndexOf('@');
  // require a local part and a domain, so 'not-an-email' and '@x.com' are
  // rejected rather than silently treated as comparable.
  if (atIndex <= 0 || atIndex === trimmed.length - 1) {
    return undefined;
  }
  let local = trimmed.slice(0, atIndex);
  const rawDomain = trimmed.slice(atIndex + 1);
  // a domain must have a dot and must not start or end with one.
  if (!rawDomain.includes('.') || rawDomain.startsWith('.') || rawDomain.endsWith('.')) {
    return undefined;
  }

  const plusIndex = local.indexOf('+');
  if (plusIndex >= 0) {
    local = local.slice(0, plusIndex);
  }
  const canonicalDomain = DOT_INSENSITIVE_DOMAIN_CANONICAL[rawDomain];
  if (canonicalDomain) {
    local = local.split('.').join('');
  }
  // a local part that was nothing but a +tag leaves nothing to compare.
  if (!local) {
    return undefined;
  }
  return `${local}@${canonicalDomain ?? rawDomain}`;
}

/**
 * Whether two addresses probably belong to the same person. Conservative by
 * design: it only says yes when both reduce to exactly the same value, so
 * two different mailboxes on a dot-sensitive domain never match.
 */
export function doEmailsLikelyBelongToSamePerson(
  a: string | undefined,
  b: string | undefined,
): boolean {
  const normalizedA = normalizeEmailForAccountMatch(a);
  const normalizedB = normalizeEmailForAccountMatch(b);
  if (!normalizedA || !normalizedB) {
    return false;
  }
  return normalizedA === normalizedB;
}

/** The minimum an account must expose to be tested for a relationship. Kept
 *  deliberately small: the caller scans many accounts, so it projects only
 *  these fields out of CouchDB rather than pulling whole user docs. */
export interface RelatedAccountCandidate {
  uid: string;
  email?: string;
  /** Every Paddle customer id (`ctm_…`) on the account — the personal
   *  subscription block and the coach per-seat block can each carry one. Not
   *  credentials; they are opaque customer handles, safe to compare and to
   *  show to an operator. */
  paddleCustomerIds?: string[];
}

/** Why a candidate was judged related. Shown to the operator so they can see
 *  the evidence rather than trusting a bare "related" label. */
export type RelatedAccountMatchReason = 'email' | 'paddleCustomer';

export interface RelatedAccount extends RelatedAccountCandidate {
  matchedOn: RelatedAccountMatchReason[];
}

/**
 * Pick, out of a set of candidate accounts, the ones that probably belong to
 * the same person as `target`.
 *
 * Two signals, either of which is enough:
 *  - the addresses reduce to the same mailbox, or
 *  - the two accounts share a Paddle customer id, which is how the
 *    #4198 / #4227 duplicates actually arise (one Paddle customer ends up
 *    owning two app accounts, often with unrelated addresses).
 *
 * The target itself is always excluded. A missing value never matches another
 * missing value — two accounts that both lack an email, or both lack a Paddle
 * customer, are not thereby related.
 */
export function selectRelatedAccounts(
  target: RelatedAccountCandidate,
  candidates: RelatedAccountCandidate[],
): RelatedAccount[] {
  const related: RelatedAccount[] = [];
  const targetPaddleIds = target.paddleCustomerIds ?? [];

  for (const candidate of candidates) {
    // never report the account being inspected as its own duplicate
    if (candidate.uid === target.uid) {
      continue;
    }

    const matchedOn: RelatedAccountMatchReason[] = [];
    if (doEmailsLikelyBelongToSamePerson(target.email, candidate.email)) {
      matchedOn.push('email');
    }
    // any overlap counts. Both lists being empty can never overlap, so two
    // accounts with no Paddle history are not matched on absence.
    const candidatePaddleIds = candidate.paddleCustomerIds ?? [];
    if (targetPaddleIds.some(id => !!id && candidatePaddleIds.includes(id))) {
      matchedOn.push('paddleCustomer');
    }

    if (matchedOn.length > 0) {
      related.push({ ...candidate, matchedOn });
    }
  }

  return related;
}

/** How many related accounts a front-end shows before summarising the rest.
 *  One person can legitimately own dozens of accounts — a heavy `+tag` user is
 *  the common case — and an uncapped list is unreadable on the admin page and
 *  unusable in a terminal. */
export const RELATED_ACCOUNT_DISPLAY_LIMIT = 10;

export interface LimitedRelatedAccounts {
  shown: RelatedAccount[];
  /** How many matches are NOT in `shown`. Rendered as "and N more" so the
   *  operator always knows the true size of the overlap. */
  hiddenCount: number;
}

/** Trim a related-account list to a readable size, keeping the total visible.
 *  Shared so the Admin UI and the CLI cap identically. */
export function limitRelatedAccountsForDisplay(
  related: RelatedAccount[],
  limit: number = RELATED_ACCOUNT_DISPLAY_LIMIT,
): LimitedRelatedAccounts {
  if (related.length <= limit) {
    return { shown: related, hiddenCount: 0 };
  }
  return { shown: related.slice(0, limit), hiddenCount: related.length - limit };
}

// ---------------------------------------------------------------------------
// The candidate-scan contract
//
// The server helper and the `firebase-cmd` CLI each run their own scan of the
// `_users` database (they authenticate differently — see the PRD's Step 1.5b),
// but they MUST agree on what they read and how many, or the two front-ends
// would report different duplicate sets. That contract lives here so it cannot
// drift, exactly like the matching rules above.
// ---------------------------------------------------------------------------

/**
 * Hard cap on how many `_users` docs one lookup will pull.
 *
 * There is no index that can express "reduces to the same mailbox", so this is
 * a scan. That is acceptable because the call is admin-only and on demand, and
 * it is the exception: the account lookups on payment paths (`findOneUser` in
 * `server/functions/src/couchdb-helpers.ts`) are all indexed. The cap stops a
 * pathological user base turning one admin page load into an unbounded read.
 *
 * NOTE the ceiling this implies: `_find` returns docs in `_id` order, so once
 * the user base exceeds this cap the scan always sees the same first slice and
 * duplicates outside it become invisible. That is why every caller must
 * surface the truncation flag rather than reporting a clean "none found". If
 * the user base approaches this number, page the scan with Mango's `bookmark`
 * instead of raising the cap.
 */
export const RELATED_ACCOUNT_SCAN_LIMIT = 20000;

/** The only fields a candidate scan may read. Keeping this list short is what
 *  guarantees no password material or provider token ever leaves CouchDB.
 *
 *  `usersDocFormatVersion` must stay in the list: this is a PROJECTED `_find`,
 *  so a field left out comes back absent, absent reads as version 1, and the
 *  scan's version check would then pass for every row including a genuinely
 *  too-new one (#4283). */
export const RELATED_ACCOUNT_SCAN_FIELDS = ['name', 'email', 'paddleData.customerId', 'coachSeatData.customerId', 'usersDocFormatVersion'];

/** Turn one projected `_users` row into a candidate, or undefined when the row
 *  is not a user account at all (a design doc has no `name`). */
export function toRelatedAccountCandidate(doc: {
  name?: string,
  email?: string,
  paddleData?: { customerId?: string },
  coachSeatData?: { customerId?: string },
} | undefined): RelatedAccountCandidate | undefined {
  // `name` is the uid on a _users doc.
  if (!doc?.name) {
    return undefined;
  }
  // a coach can hold a personal Paddle subscription AND a coach per-seat one;
  // either customer id can be the link to a duplicate account.
  const paddleCustomerIds = [doc.paddleData?.customerId, doc.coachSeatData?.customerId]
    .filter((id): id is string => !!id);
  return {
    uid: doc.name,
    email: doc.email,
    paddleCustomerIds: paddleCustomerIds.length > 0 ? paddleCustomerIds : undefined,
  };
}
